import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { StatusEgressState as S } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { locateProxy } from '#~/agent/utils.js';
import { createAlertTracker, ALERT_ACTION } from '#~/analytics/domains/alert-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import { depot } from '#~/depot/index.js';
import {
  getIncident,
  raise,
  resolve,
  subscribeIncidents,
  supersede,
  type Incident,
  type IncidentKey
} from '#~/notifications/incidents.js';
import { post } from '#~/notifications/utils.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { Context } from '#~/room/provider/context.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { useOutputting } from './output/use-outputting.js';

/** Observe NDI for every studio participant; production controls remain permission-gated. */
export const useNDIOutputNotices = (
  canControl: boolean,
  openOutputSettings: (deviceId: Device['id'], agentId: Agent['id']) => void
): void => {
  const roomId = useContext(Context);
  const { track } = useAnalytics();
  const tracker = useMemo(() => createAlertTracker(track), [track]);
  const { devices } = useOutputting();
  const { enabled, loading } = useFeatureFlag('alert_n9_enabled', { defaultValue: true });
  const previous = useRef<Record<string, DeviceEgressSelectorType>>({});
  const failures = useRef(new Set<string>());
  const identities = useRef(new Map<string, { key: IncidentKey; agentId: Agent['id'] }>());
  const actionContext = useRef({ canControl, openOutputSettings, roomId });

  const syncAction = useCallback((incident: Incident, agentId: Agent['id']) => {
    if (!incident.entity) return;
    const deviceId = incident.entity as Device['id'];
    depot.getState().produce((draft) => {
      const entry = draft.notifications[incident.notificationId];
      if (!entry || entry[2].isClosed) return;
      // Update only the action: a role change is not a new showing and must
      // preserve dismissal, cooldown and lifecycle identity.
      entry[1] = {
        ...entry[1],
        actions: actionContext.current.canControl
          ? [
              {
                action: ALERT_ACTION.OPEN_OUTPUT_SETTINGS,
                label: 'Open output settings',
                onClick: () => {
                  const current = actionContext.current;
                  // A button callback captured before revocation cannot grant access.
                  if (current.canControl && current.roomId === incident.audience) {
                    current.openOutputSettings(deviceId, agentId);
                  }
                }
              }
            ]
          : []
      };
    });
  }, []);

  useLayoutEffect(() => {
    actionContext.current = { canControl, openOutputSettings, roomId };
    for (const { key, agentId } of identities.current.values()) {
      const incident = getIncident(key);
      if (incident) syncAction(incident, agentId);
    }
    return () => {
      actionContext.current.canControl = false;
    };
  }, [canControl, openOutputSettings, roomId, syncAction]);

  useEffect(() => {
    const unsubscribe = subscribeIncidents((event) => {
      const { incident } = event;
      if (incident.alert !== 'n9' || incident.audience !== roomId) return;
      const identity = identities.current.get(incident.entity ?? '');
      if (!identity || !roomId) return;
      const showing = {
        alert_id: 'n9' as const,
        showing_id: incident.showingId,
        studio_id: roomId,
        agent_id: identity.agentId
      };
      // ENG-4930 emits shown only after delivery; a cancelled pending notice
      // never produces display telemetry. Keep all events on that lifecycle.
      if (event.type === 'shown') {
        // Delivery may follow a permission change while the post was queued.
        syncAction(incident, identity.agentId);
        tracker.trackAlertShown(showing);
      } else if (event.type === 'closed') tracker.trackAlertClosed(showing, event.reason);
      else if (event.action === ALERT_ACTION.OPEN_OUTPUT_SETTINGS) {
        tracker.trackAlertActed(showing, { action: ALERT_ACTION.OPEN_OUTPUT_SETTINGS });
      }
    });
    return () => {
      // A notice from the previous studio must not open settings in the next one.
      for (const { key } of identities.current.values()) supersede(key);
      unsubscribe();
      identities.current.clear();
      failures.current.clear();
      previous.current = {};
    };
  }, [roomId, tracker, syncAction]);

  useEffect(() => {
    const prevMap = previous.current;
    previous.current = devices;
    if (!roomId) return;
    if (!loading && enabled === false) {
      for (const { key } of identities.current.values()) supersede(key);
    }
    for (const [deviceId, current] of Object.entries(devices)) {
      const now = current.egress?.ndiOutputStatus?.state;
      let recovered = false;
      // Reconnects can restore an active output without a previous snapshot.
      // Reconcile known problems before checking transitions for legacy posts.
      if (now === S.EGRESS_STATE_ACTIVE) {
        const identity = identities.current.get(deviceId);
        if (identity) resolve(identity.key);
        recovered = failures.current.delete(deviceId);
      }
      const prev = prevMap[deviceId];
      if (!prev) continue;
      const before = prev.egress?.ndiOutputStatus?.state;
      if (before === now) continue;

      if (now === S.EGRESS_STATE_ACTIVE) {
        if (canControl && !(enabled === true && recovered)) {
          post({
            title: 'NDI Output started',
            message: `${current.name} is now outputting via NDI.`,
            severity: 'success'
          });
        }
      }
      if (
        canControl &&
        before !== undefined &&
        before !== S.EGRESS_STATE_INACTIVE &&
        now === S.EGRESS_STATE_INACTIVE
      ) {
        post({
          title: 'NDI Output stopped',
          message: `${current.name} has stopped outputting via NDI.`,
          severity: 'success'
        });
      }
      if (now !== S.EGRESS_STATE_FAILED && now !== S.EGRESS_STATE_FAILED_TO_START) continue;
      // Consume loading-time transitions rather than replaying them when the
      // flag arrives. Off preserves the existing notices and their audience.
      if (loading || enabled === undefined) continue;
      const backendError = current.egress?.ndiOutputStatus?.error;
      if (!enabled) {
        if (!canControl) continue;
        const failedToStart = now === S.EGRESS_STATE_FAILED_TO_START;
        post({
          actionable: true,
          title: failedToStart ? 'NDI Output failed to start' : 'NDI Output failed',
          message: backendError
            ? `${current.name}: ${backendError}`
            : failedToStart
              ? `${current.name} failed to start NDI output. Check your settings and try again.`
              : `${current.name} unexpectedly stopped outputting via NDI.`,
          severity: 'error'
        });
        continue;
      }
      const proxy = locateProxy(current.id);
      if (!proxy || proxy.roomId !== roomId) continue;
      const key = { alert: 'n9', entity: deviceId, audience: roomId } as const;
      identities.current.set(deviceId, { key, agentId: proxy.id });
      failures.current.add(deviceId);
      raise(
        key,
        {
          actionable: true,
          title: 'NDI output stopped',
          // Retain the existing backend detail after the required sentence until
          // Design signs off its disposition (ENG-4867 Before it ships).
          message: `${current.name} isn't sending anymore.${backendError ? ` ${backendError}` : ''}`,
          severity: 'error'
        },
        {
          autoClose: false
        }
      );
    }
  }, [devices, enabled, loading, canControl, roomId, openOutputSettings]);
};
